/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type Nudge } from '@atlassian/ai-smart-button-api';
import { type ConversationAction } from '@atlassian/conversation-assistant';

export const i18n = defineMessages({
	aiProactiveNudgeDismiss: {
		id: 'ai-smart-button.ai-proactive-nudge.dismiss',
		defaultMessage: 'Dismiss',
		description: 'The label text for the dismiss button on the AI Proactive nudge',
	},
});

const ANIMATION_DURATION = 200;

const styles = cssMap({
	wrapperStyles: {
		position: 'absolute',
		left: 0,
		'@media (prefers-reduced-motion)': {
			animation: 'none',
		},
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		borderRadius: token('radius.large'),
		display: 'inline-block',
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface.raised'),
		transition: `opacity ${ANIMATION_DURATION}ms ease-in-out, transform ${ANIMATION_DURATION}ms ease-in-out`,
	},
	newWrapperStyles: {
		position: 'absolute',
		left: 0,
		'@media (prefers-reduced-motion)': {
			animation: 'none',
		},
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		borderRadius: token('radius.large'),
		display: 'inline-block',
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('color.background.neutral.bold.hovered'),
		transition: `opacity ${ANIMATION_DURATION}ms ease-in-out, transform ${ANIMATION_DURATION}ms ease-in-out`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	newNudgeIconStyles: {
		color: token('color.icon.inverse'),
	},
	invisibleNudge: {
		opacity: 0,
		transform: `translateX(-100%)`,
	},
	visibleNudge: {
		transform: `translateX(calc(-100% - ${token('space.100')}))`,
		opacity: 1,
	},
});

type NudgeContentProps = {
	activeNudge: Nudge | ConversationAction;
	isNudgeVisible: boolean;
	onDismissClick: () => void;
	onNudgeClick: () => void;
	onWrapperMouseEnter: () => void;
	onWrapperMouseLeave: () => void;
};

export const NudgeContent = memo(
	({
		activeNudge,
		isNudgeVisible,
		onWrapperMouseEnter,
		onWrapperMouseLeave,
		onNudgeClick,
		onDismissClick,
	}: NudgeContentProps) => {
		const intl = useIntl();
		const useNewNudgeAppearance = FeatureGates.getExperimentValue(
			'confluence_rovo_button_nudge_contrast',
			'isEnabled',
			false,
		);

		const NudgeIcon = (activeNudge as Nudge).nudgeIcon ?? (activeNudge as ConversationAction).icon;

		return (
			/**
			 * Wrapper div is used to handle mouse enter and leave events
			 *  because dismiss timer needs to be stopped when
			 * 	user hover over anywhere in nudge.
			 *  Listnening only on button or icon, will not stop timer when
			 * 	hovering over th edges of the nudge.
			 */
			// eslint-disable-next-line jsx-a11y/no-static-element-interactions
			<div
				css={[
					useNewNudgeAppearance ? styles.newWrapperStyles : styles.wrapperStyles,
					isNudgeVisible ? styles.visibleNudge : styles.invisibleNudge,
				]}
				data-testid="platform-ai-proactive-nudge"
				onMouseEnter={onWrapperMouseEnter}
				onMouseLeave={onWrapperMouseLeave}
			>
				<Inline grow="fill" space="space.025">
					<Stack alignInline="end">
						<Button appearance="subtle" onClick={onNudgeClick}>
							<Inline space="space.075" alignBlock="end">
								<div css={useNewNudgeAppearance && styles.newNudgeIconStyles}>
									<NudgeIcon label="" />
								</div>
								<Text color={useNewNudgeAppearance ? 'color.text.inverse' : undefined}>
									{activeNudge.title}
								</Text>
							</Inline>
						</Button>
					</Stack>
					<IconButton
						testId="platform-ai-proactive-nudge-dismiss"
						appearance="subtle"
						label={intl.formatMessage(i18n.aiProactiveNudgeDismiss)}
						isTooltipDisabled={false}
						tooltip={{ position: 'top' }}
						icon={(iconProps) => (
							<CrossIcon
								{...iconProps}
								size="small"
								color={useNewNudgeAppearance ? token('color.icon.inverse') : undefined}
							/>
						)}
						onClick={onDismissClick}
					/>
				</Inline>
			</div>
		);
	},
);
